import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, X, MapPin, Mail, Upload, Users } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ClientFormModal from '../components/ClientFormModal';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';

// Fix default leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function waLink(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  const number = digits.startsWith('0') ? '27' + digits.slice(1) : digits;
  return `https://wa.me/${number}`;
}

function WhatsAppIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function TelegramIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

function MapModal({ client, onClose }) {
  const lat = parseFloat(client.latitude);
  const lng = parseFloat(client.longitude);
  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden m-auto my-4">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <p className="font-semibold text-gray-800">{client.name}</p>
            <p className="text-xs text-gray-400">{client.address || 'Pinned location'}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer"
              className="text-xs text-orange-500 hover:underline">Open in Google Maps</a>
            <button onClick={onClose} className="ml-2"><X size={18} className="text-gray-400" /></button>
          </div>
        </div>
        <div style={{ height: 350 }}>
          <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            {/* Street map or satellite — the layer button top-right switches */}
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Street">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='© <a href="https://www.esri.com">Esri</a> World Imagery' maxZoom={19} />
              </LayersControl.BaseLayer>
            </LayersControl>
            <Marker position={[lat, lng]}>
              <Popup>{client.name}{client.address ? <><br />{client.address}</> : ''}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

const rand = (n) => (Math.round(n * 100) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2 });

// Step 1 — clients from the Customer Contact List
function ClientsImportTab({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (commit) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/clients/import-quickbooks${commit ? '?commit=true' : ''}`, fd);
      if (commit) { setResult(r.data); onDone && onDone(); } else { setPreview(r.data); }
    } catch (e) { toast.error(e.response?.data?.error || 'Import failed'); }
    finally { setBusy(false); }
  };

  const s = preview?.summary;
  if (result) return (
    <div className="text-sm space-y-2">
      <div className="bg-green-50 text-green-800 rounded-lg p-3">
        <div className="font-medium mb-1">Clients imported ✓</div>
        <div>{result.imported} new · {result.updated} updated (blanks filled only)</div>
        {result.balances_skipped
          ? <div className="text-gray-500">No balances created — bring debt over on the Open invoices tab</div>
          : <div>{result.opening_invoices} opening balances · R{rand(result.opening_total)}</div>}
      </div>
      <p className="text-xs text-gray-500">Next: use the <b>Open invoices</b> tab to bring in accurate 30/60/90 ageing.</p>
    </div>
  );
  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Upload the <b>Customer Contact List</b> (CSV). Adds new clients and fills in blanks on existing ones —
        it never overwrites details you've already captured, so it's <b>safe to re-run</b> as often as you like
        while staff keep working in QuickBooks. Money is not touched here; bring debt over on the
        <b> Open invoices</b> tab.
      </p>
      <input type="file" accept=".csv,text/csv"
        onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }}
        className="block w-full text-sm mb-3 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-orange-50 file:text-orange-600 file:text-sm" />
      {s && (
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 mb-3">
          <div className="flex justify-between"><span className="text-gray-500">Clients found</span><b>{s.total}</b></div>
          <div className="flex justify-between"><span className="text-gray-500">With contact details</span><span>{s.with_contact}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">With VAT number</span><span>{s.with_vat}</span></div>
          <div className="flex justify-between border-t pt-1 mt-1"><span className="text-gray-500">Opening balances</span><b>{s.positive_balance_count} · R{rand(s.positive_balance_total)}</b></div>
        </div>
      )}
      {!preview ? (
        <button disabled={!file || busy} onClick={() => send(false)}
          className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {busy ? 'Reading…' : 'Preview'}
        </button>
      ) : (
        <button disabled={busy} onClick={() => send(true)}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {busy ? 'Importing…' : `Import ${s.total} clients`}
        </button>
      )}
    </>
  );
}

// Superseded by FullHistoryImportTab (it would delete receipts on OB- invoices
// after a full import). Kept out of the tab list; the route still exists.
// eslint-disable-next-line no-unused-vars
function OpenInvoicesImportTab({ onDone }) {
  const [files, setFiles] = useState({ invoices: null, payments: null, credits: null });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const setF = (k) => (e) => { setFiles((f) => ({ ...f, [k]: e.target.files[0] })); setPreview(null); };

  const send = async (commit, discardPayments) => {
    if (!files.invoices) return toast.error('The invoice CSV is required');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('invoices', files.invoices);
      if (files.payments) fd.append('payments', files.payments);
      if (files.credits) fd.append('credits', files.credits);
      const qs = commit ? `?commit=true${discardPayments ? '&discard_payments=true' : ''}` : '';
      const r = await api.post(`/clients/import-open-invoices${qs}`, fd);
      if (commit) { setResult(r.data); onDone && onDone(); } else { setPreview(r.data); }
    } catch (e) {
      // 409 = receipts already recorded against the balances we'd replace
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.existing) {
        if (window.confirm(`${d.error}\n\nReplace anyway?`)) return send(true, true);
      } else {
        toast.error(d?.error || 'Import failed');
      }
    }
    finally { setBusy(false); }
  };

  const s = preview?.summary || result?.summary;
  const Row = ({ k, label, required }) => (
    <div className="mb-2">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}{files[k] && <span className="text-green-600"> ✓</span>}</label>
      <input type="file" accept=".csv,text/csv" onChange={setF(k)}
        className="block w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-orange-50 file:text-orange-600" />
    </div>
  );

  if (result) return (
    <div className="text-sm space-y-2">
      <div className="bg-green-50 text-green-800 rounded-lg p-3">
        <div className="font-medium mb-1">Open invoices imported ✓</div>
        <div>{result.imported} dated invoices · R{rand(result.open_total)} across {result.customers} clients</div>
        {result.unmatched?.length > 0 && <div className="text-amber-700">{result.unmatched.length} customers had no matching client (import the contact list first)</div>}
      </div>
      <p className="text-xs text-gray-500">Debtors now ages by each invoice's real date. Check it against your QuickBooks A/R aging report.</p>
    </div>
  );
  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        For accurate <b>30/60/90 ageing</b>. Upload three QuickBooks reports; we rebuild which invoices are
        still unpaid (applying payments oldest-first) and record each with its real date. <b>Replaces</b> the
        lump opening balances. Run the Clients tab first so names match.
      </p>
      <Row k="invoices" label="Invoice list CSV" required />
      <Row k="payments" label="Payments CSV" />
      <Row k="credits" label="Credit-memo CSV" />
      {s && (
        <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 my-3">
          <div className="flex justify-between"><span className="text-gray-500">Open invoices</span><b>{s.open_invoice_count} · R{rand(s.open)}</b></div>
          <div className="flex justify-between"><span className="text-gray-500">Clients owing</span><span>{s.customer_count}</span></div>
          <div className="border-t pt-1 mt-1 text-xs text-gray-500">Ageing</div>
          <div className="flex justify-between"><span className="text-gray-500">Current (0–30)</span><span>R{rand(s.ageing.d0_30)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">31–60 days</span><span>R{rand(s.ageing.d31_60)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">61–90 days</span><span>R{rand(s.ageing.d61_90)}</span></div>
          <div className="flex justify-between"><span className="text-red-600">90+ days</span><b className="text-red-600">R{rand(s.ageing.d90p)}</b></div>
          {s.existing?.opening_invoices > 0 && (
            <div className="border-t pt-1 mt-1 text-xs text-amber-700">
              Replaces {s.existing.opening_invoices} existing opening-balance invoice(s).
              {s.existing.payments_on_them > 0 && (
                <b> {s.existing.payments_on_them} payment(s) of R{rand(s.existing.payments_total)} recorded against them will be removed — you'll be asked to confirm.</b>
              )}
            </div>
          )}
        </div>
      )}
      {!preview ? (
        <button disabled={!files.invoices || busy} onClick={() => send(false)}
          className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 mt-2">
          {busy ? 'Reading…' : 'Preview'}
        </button>
      ) : (
        <button disabled={busy} onClick={() => send(true)}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 mt-2">
          {busy ? 'Importing…' : `Import ${s.open_invoice_count} dated invoices`}
        </button>
      )}
    </>
  );
}

// Step 2 — everything QuickBooks holds, so it can be switched off: every
// invoice with its lines, every payment and every credit memo. Replaces the
// QuickBooks layer only; what was raised in Expressway is never touched.
function FullHistoryImportTab({ onDone }) {
  const [files, setFiles] = useState({ invoices: null, payments: null, credits: null, lines: null, balances: null });
  const [qbTotal, setQbTotal] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const setF = (k) => (e) => { setFiles((f) => ({ ...f, [k]: e.target.files[0] })); setPreview(null); };

  const send = async (commit, allowShrink) => {
    if (!files.invoices) return toast.error('The invoice CSV is required');
    setBusy(true);
    try {
      const fd = new FormData();
      for (const k of ['invoices', 'payments', 'credits', 'lines', 'balances']) if (files[k]) fd.append(k, files[k]);
      if (qbTotal !== '') fd.append('qb_total', qbTotal);
      const qs = commit ? `?commit=true${allowShrink ? '&allow_shrink=true' : ''}` : '';
      const r = await api.post(`/clients/import-history${qs}`, fd);
      if (commit) { setResult(r.data); onDone && onDone(); } else { setPreview(r.data); }
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.shrink) {
        if (window.confirm(`${d.error}\n\nImport the smaller file anyway?`)) return send(true, true);
      } else {
        toast.error(d?.error || 'Import failed');
      }
    } finally { setBusy(false); }
  };

  const s = preview?.summary || result?.summary;
  const Row = ({ k, label, required, hint }) => (
    <div className="mb-2">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}{files[k] && <span className="text-green-600"> ✓</span>}</label>
      <input type="file" accept=".csv,text/csv" onChange={setF(k)}
        className="block w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-orange-50 file:text-orange-600" />
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
  const Stat = ({ label, value, tone }) => (
    <div className={`rounded-lg p-2 ${tone === 'bad' ? 'bg-red-50 text-red-800' : tone === 'good' ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-700'}`}>
      <div className="text-[11px] opacity-70">{label}</div><div className="font-semibold text-sm">{value}</div>
    </div>
  );
  const Table = ({ title, rows, cols }) => rows?.length > 0 && (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer font-medium text-gray-700">{title} ({rows.length})</summary>
      <div className="max-h-48 overflow-auto border rounded mt-1">
        <table className="w-full text-[11px]">
          <tbody>{rows.map((r, i) => <tr key={i} className="border-b last:border-0">{cols.map((c) => <td key={c} className="px-2 py-1">{typeof r[c] === 'number' ? rand(r[c]) : String(r[c] ?? '')}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </details>
  );

  if (result) return (
    <div className="text-sm space-y-2">
      <div className="bg-green-50 text-green-800 rounded-lg p-3">
        <div className="font-medium mb-1">QuickBooks history imported ✓</div>
        <div>{result.imported.invoices} invoices ({result.imported.items} lines) · {result.imported.payments} payment rows · {result.imported.credit_notes} credit notes</div>
        {result.imported.kept > 0 && <div>{result.imported.kept} Expressway receipts/credits/emails re-attached to their invoices</div>}
        {result.imported.orphaned > 0 && <div className="text-amber-700 font-medium">{result.imported.orphaned} Expressway rows sat on invoices that are no longer in the export — see the QB-HOLD invoice and reallocate them.</div>}
        {result.unmatched_clients?.length > 0 && <div className="text-amber-700">{result.unmatched_clients.length} customers had no matching client (import the contact list first): {result.unmatched_clients.slice(0, 8).join(', ')}{result.unmatched_clients.length > 8 ? '…' : ''}</div>}
      </div>
      <p className="text-xs text-gray-500">Check the Debtors page against your QuickBooks Customer Balance Summary. Old invoices now print their QuickBooks lines.</p>
    </div>
  );
  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        Everything in QuickBooks, so it can be switched off. Export each report with <b>Dates = All</b> as CSV.
        The QuickBooks layer is replaced by these files; invoices, receipts and credit notes made in
        Expressway are kept. Run the Clients tab first so names match.
      </p>
      <Row k="invoices" label="Invoices CSV" required hint="Custom Transaction Detail, type Invoice: Name, Num, Date, Due Date, Amount" />
      <Row k="payments" label="Payments CSV" hint="type Payment: Name, Num, Date, Pay Meth, Amount" />
      <Row k="credits" label="Credit memos CSV" hint="type Credit Memo: Name, Num, Date, Amount" />
      <Row k="lines" label="Invoice lines CSV" hint="Sales by Customer Detail: Type, Date, Num, Memo, Name, Item, Qty, Sales Price, Amount — puts the lines on each invoice" />
      <Row k="balances" label="Customer balances CSV (check only)" hint="Customer Balance Detail or Summary — compared per client, nothing is set from it" />
      <div className="mb-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">QuickBooks total owed (Customer Balance Summary) — optional check</label>
        <input type="number" step="0.01" value={qbTotal} onChange={(e) => { setQbTotal(e.target.value); setPreview(null); }}
          className="w-40 border rounded px-2 py-1 text-sm" placeholder="e.g. 195854.37" />
      </div>
      {s && (
        <div className="my-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Invoices in file" value={`${s.invoices} · R${rand(s.invoice_total)}`} />
            <Stat label="New since last import" value={`${s.new_invoices} · R${rand(s.new_invoice_total)}`} tone={s.new_invoices ? 'good' : undefined} />
            <Stat label="Payments / credit notes" value={`${s.payments} · ${s.credit_notes}`} />
            <Stat label="Still open" value={`${s.open_items} · R${rand(s.open_total)}`} />
            <Stat label="With lines / without" value={`${s.with_lines} / ${s.without_lines}`} tone={files.lines && s.without_lines ? 'bad' : undefined} />
            <Stat label="Lines that don't add up" value={s.line_mismatches} tone={s.line_mismatches ? 'bad' : 'good'} />
            {s.qb_total != null && (
              <Stat label="vs QuickBooks total owed" value={Math.abs(s.qb_total_delta) < 1 ? 'matches ✓' : `off by R${rand(s.qb_total_delta)}`} tone={Math.abs(s.qb_total_delta) < 1 ? 'good' : 'bad'} />
            )}
            {s.existing?.invoices > 0 && <Stat label="Already imported (replaced)" value={`${s.existing.invoices} invoices`} />}
            {s.balances_checked > 0 && <Stat label="Clients whose balance differs" value={`${s.clients_off} of ${s.balances_checked}`} tone={s.clients_off ? 'bad' : 'good'} />}
            {s.vat_before_registration > 0 && <Stat label="VAT lines before the VAT date" value={s.vat_before_registration} tone="bad" />}
          </div>
          {s.existing?.live_payments > 0 && <p className="text-xs text-gray-600">{s.existing.live_payments} receipt(s) captured in Expressway against QuickBooks invoices will be kept and re-attached.</p>}
          {s.line_stats?.vat_item_lines > 0 && <p className="text-xs text-amber-700">{s.line_stats.vat_item_lines} lines are VAT items from QuickBooks — they are imported as lines; tell us if they should be dropped.</p>}
          {s.vat_before_registration > 0 && <p className="text-xs text-amber-700">{s.vat_before_registration} invoices dated before the VAT date ({s.vat_from}) have lines that add up ex VAT — QuickBooks charged VAT on them. Check the VAT date in Settings before importing.</p>}
          <Table title="Clients whose balance differs from QuickBooks" rows={preview?.client_deltas} cols={['client', 'quickbooks', 'import', 'delta', 'note']} />
          <Table title="Invoices QuickBooks and Expressway may both hold" rows={preview?.possible_duplicates} cols={['client', 'qb_num', 'qb_date', 'expressway', 'date', 'total']} />
          <Table title="Lines that don't add up to the invoice" rows={preview?.line_mismatches} cols={['client', 'num', 'date', 'total', 'line_sum']} />
          <Table title="New invoices this run" rows={preview?.new_invoices} cols={['client', 'num', 'date', 'total', 'lines']} />
          <Table title="Receipts with an unrecognised reference prefix" rows={preview?.unmatched_payments} cols={['client', 'reference', 'date', 'amount', 'method']} />
        </div>
      )}
      {!preview ? (
        <button disabled={!files.invoices || busy} onClick={() => send(false)}
          className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 mt-2">
          {busy ? 'Reading…' : 'Preview'}
        </button>
      ) : (
        <button disabled={busy} onClick={() => send(true)}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 mt-2">
          {busy ? 'Importing…' : `Import ${s.invoices} invoices · ${s.payments} payments · ${s.credit_notes} credit notes`}
        </button>
      )}
    </>
  );
}

function QuickBooksImport({ onClose, onDone }) {
  const [tab, setTab] = useState('clients');
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 m-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800">Import from QuickBooks</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {[['clients', '1 · Clients'], ['history', '2 · Full history']].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${tab === k ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'}`}>
              {lbl}
            </button>
          ))}
        </div>
        {tab === 'clients' ? <ClientsImportTab onDone={onDone} /> : <FullHistoryImportTab onDone={onDone} />}
      </div>
    </div>
  );
}

export default function Clients() {
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [mapClient, setMapClient] = useState(null);
  const qc = useQueryClient();

  const [sort, setSort] = useState('name'); // 'name' | 'recent'

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients', search, sort],
    queryFn: () => api.get('/clients', {
      params: { search: search || undefined, sort: sort === 'recent' ? 'recent' : undefined },
    }).then(r => r.data),
  });

  // The whole client book now loads (it used to stop at 100), so page it here
  // rather than scrolling hundreds of rows. Searching resets to the first page.
  const PER_PAGE = 50;
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(clients.length / PER_PAGE);
  const shown = clients.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const onSearch = (v) => { setSearch(v); setPage(0); };
  const onSort = (v) => { setSort(v); setPage(0); };

  // ClientFormModal refreshes the client caches itself.
  const saved = () => setModal(null);

  return (
    <div className="space-y-4">
      {mapClient && <MapModal client={mapClient} onClose={() => setMapClient(null)} />}
      {modal && <ClientFormModal existing={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={saved} />}
      {importOpen && <QuickBooksImport onClose={() => setImportOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ['clients'] })} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Clients</h1>
          <p className="text-xs text-gray-400">All senders and receivers</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {can('parcels.assign') && <Link to="/clients/missing-addresses"
            className="flex items-center gap-1 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
            <MapPin size={15} /> Missing addresses
          </Link>}
          {can('clients.duplicates') && <Link to="/clients/duplicates"
            className="flex items-center gap-1 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
            <Users size={15} /> Duplicates
          </Link>}
          {can('clients.admin') && <button onClick={() => setImportOpen(true)}
            className="flex items-center gap-1 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
            <Upload size={15} /> QuickBooks import
          </button>}
          {can('clients.create') && <button onClick={() => setModal('add')}
            className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Plus size={16} /> Add Client
          </button>}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search name, business, phone, email, code…"
            value={search} onChange={e => onSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        {/* "Newest first" is how you confirm a client you just added actually
            landed — alphabetically they disappear into 400 others. */}
        <div className="flex gap-1">
          {[['name', 'A–Z'], ['recent', 'Newest first']].map(([key, lbl]) => (
            <button key={key} type="button" onClick={() => onSort(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                sort === key ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
              }`}>
              {lbl}
            </button>
          ))}
        </div>
        {!isLoading && (
          <span className="text-xs text-gray-400">
            {clients.length} client{clients.length === 1 ? '' : 's'}
            {pageCount > 1 && ` · showing ${page * PER_PAGE + 1}–${Math.min((page + 1) * PER_PAGE, clients.length)}`}
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading...</div> : (
          <>
          <div className="md:hidden divide-y">
            {shown.map(c => (
              <div key={c.id} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/clients/${c.id}`} className="font-medium text-gray-800 hover:text-orange-600">{c.name}</Link>
                    {c.business_name && c.business_name !== c.name && <p className="text-xs text-gray-400">{c.business_name}</p>}
                    {c.address && <p className="text-xs text-gray-500 truncate">{c.address}</p>}
                  </div>
                  <button onClick={() => setModal(c)} className="text-xs text-orange-500 border border-orange-200 rounded-lg px-3 py-1.5 whitespace-nowrap">Edit</button>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-600">
                  {c.phone && (
                    <span className="inline-flex items-center gap-1.5">
                      <a href={waLink(c.phone)} target="_blank" rel="noreferrer" className="text-green-600"><WhatsAppIcon size={14} /></a>
                      <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>
                    </span>
                  )}
                  {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-blue-600 truncate"><Mail size={13} /> {c.email}</a>}
                  {c.latitude && <button onClick={() => setMapClient(c)} className="inline-flex items-center gap-1 text-green-600"><MapPin size={13} /> map</button>}
                  <span className="inline-flex items-center gap-1.5 ml-auto">
                    {c.notify_email    && <span className="text-blue-500"><Mail size={13} /></span>}
                    {c.notify_whatsapp && <span className="text-green-500"><WhatsAppIcon size={13} /></span>}
                    {c.notify_telegram && <span className="text-sky-500"><TelegramIcon size={13} /></span>}
                  </span>
                </div>
              </div>
            ))}
            {clients.length === 0 && <p className="px-4 py-8 text-center text-gray-400 text-sm">{search ? `No client matches “${search}”` : 'No clients yet'}</p>}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Address</th>
                  <th className="px-4 py-3 text-center">GPS</th>
                  <th className="px-4 py-3 text-center">Notified via</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {shown.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/clients/${c.id}`} title="Open this client's account — invoices and statements"
                        className="text-gray-800 hover:text-orange-600 hover:underline">
                        {c.name}
                      </Link>
                      {c.business_name && c.business_name !== c.name && (
                        <p className="text-xs text-gray-400 font-normal">{c.business_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.phone ? (
                        <div className="flex items-center gap-1.5">
                          <a href={waLink(c.phone)} target="_blank" rel="noreferrer"
                            title={`WhatsApp ${c.phone}`} className="text-green-600 hover:text-green-700">
                            <WhatsAppIcon size={14} />
                          </a>
                          <a href={`tel:${c.phone}`} title={`Call ${c.phone}`}
                            className="text-gray-700 hover:text-orange-500 hover:underline">
                            {c.phone}
                          </a>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.email ? (
                        <div className="flex items-center gap-1.5">
                          <a href={`mailto:${c.email}`} title={`Email ${c.email}`}
                            className="text-blue-500 hover:text-blue-600">
                            <Mail size={13} />
                          </a>
                          <span>{c.email}</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{c.address||'—'}</td>
                    <td className="px-4 py-3 text-center">
                      {c.latitude
                        ? <button onClick={() => setMapClient(c)} title="View on map"><MapPin size={15} className="text-green-500 mx-auto hover:text-orange-500" /></button>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {c.notify_email    && <span title="Email notifications"    className="text-blue-500"><Mail size={15} /></span>}
                        {c.notify_whatsapp && <span title="WhatsApp notifications" className="text-green-500"><WhatsAppIcon size={15} /></span>}
                        {c.notify_telegram && <span title="Telegram notifications" className="text-sky-500"><TelegramIcon size={15} /></span>}
                        {!c.notify_email && !c.notify_whatsapp && !c.notify_telegram && <span className="text-gray-300 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setModal(c)} className="text-xs text-orange-500 hover:underline">Edit</button>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  {search ? `No client matches “${search}”` : 'No clients yet'}
                </td></tr>}
              </tbody>
            </table>
          </div>
          </>
        )}
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <p className="text-xs text-gray-500">Page {page + 1} of {pageCount}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1.5 rounded border text-xs text-gray-600 disabled:opacity-40 hover:bg-white">Previous</button>
              <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                className="px-3 py-1.5 rounded border text-xs text-gray-600 disabled:opacity-40 hover:bg-white">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
